'use client';

import { useCaptureException } from '@kit/monitoring/hooks';
import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import { FehlerKennung } from './_components/fehler-kennung';
import { FehlerRaster } from './_components/fehler-raster';
import { PageErrorTracker } from './_components/page-error-tracker';

/**
 * Blatt P2, Fehlerart 2 — der Fehlschlag, der bleibt.
 *
 * 🔴 P2: „Jede der vier Arten endet mit **etwas Anklickbarem**: richtiger Wert,
 * nochmal versuchen, Bereich wechseln. Ein Fehler, der nur mitteilt, verschiebt
 * das Problem auf den Support."
 *
 * Hier fehlte genau das Naheliegendste: **„Nochmal versuchen".** Die Seite bot
 * „Zurück" und „Zur Startseite" — beides führt WEG von dem, was man tun wollte.
 * Bei einem vorübergehenden Fehler (Netz, Zeitüberschreitung) ist ein erneuter
 * Versuch der richtige Weg, und Next.js reicht ihn als `reset` herein.
 *
 * Zweitens die Kennung: P2 nennt sie „den **einzigen** erlaubten technischen
 * Rest" — klein und grau, zum Kopieren, damit der Support etwas hat. `digest`
 * liegt bei serverseitigen Fehlern bereits an und wurde nicht angezeigt.
 *
 * Drittens die Illustration: ein rotierender Kompass über einem Gebäudesymbol,
 * 30 Zeilen. P3: „Keine gezeichneten Illustrationen … sie machen einen Zustand
 * gross, den man möglichst schnell verlassen will."
 */
function ErrorPage({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  /*
   * 🔴 Next reicht ZWEI Wege herein, und nur einer davon ist der richtige.
   *
   * `reset()` setzt blank `this.setState({ error: null })`. `unstable_retry()`
   * macht `startTransition(() => { context.refresh(); reset(); })` —
   * Neuladen ZUERST, Zuruecksetzen danach, beides in einer Transition
   * (`next/dist/client/components/error-boundary.js:42-48`). Beide stehen in
   * `ErrorInfo` (`error-boundary.d.ts:3-7`), sind also oeffentlich.
   *
   * Der Unterschied ist nicht kosmetisch: ohne Transition greift `reset()`
   * sofort, die Kinder rendern gegen die zwischengespeicherte RSC-Nutzlast —
   * und die traegt bei einem serverseitig geworfenen Fehler den Fehler noch.
   * Die Grenze faengt erneut, bevor das nachgereichte `refresh()` ueberhaupt
   * ankommt. Genau so stand es hier (`reset(); router.refresh();`).
   */
  unstable_retry: () => void;
}) {
  // Next.js hängt `digest` nur an Fehler, die serverseitig entstanden sind —
  // die sind dort bereits erfasst. Doppelt melden wäre Rauschen.
  useCaptureException(error.digest ? null : error);

  return (
    <div className="bg-background flex min-h-screen flex-col">
      {/*
        🔴 `errorMessage` und `errorStack` fehlten hier — und nur hier.
        `PageErrorTracker` schreibt ohne sie `errorMessage: 'Unknown Error'` und
        keinen Stack; alle Berichte dieser Grenze fielen damit auf EINE
        Signatur zusammen. Das ist die Grenze mit der groessten Reichweite, und
        `admin/error.tsx`, `global-error.tsx` und beide `billing/error.tsx`
        reichen die Werte weiter.
      */}
      <PageErrorTracker
        errorType="error"
        errorMessage={error.message}
        errorStack={error.stack}
      />

      {/*
        ═══════════════════════════════════════════════════════════════════════
        🔴 BLATT P7 (03.09.2026) — weisser Grund, Kachelraster, kein Versprechen
        ═══════════════════════════════════════════════════════════════════════

        Drei Dinge sind hier anders als vorher, und jedes hat im Blatt eine
        Begruendung, die ueber diese Seite hinausgeht:

        1 · KEIN RING. „Ein drehender Kreis heisst «läuft noch». Auf einer
            Fehlerseite läuft nichts mehr; sie ist das Ergebnis. Ein Endlos-Ring
            lässt einen warten, wo nichts kommt — und man wartet tatsächlich,
            weil das Zeichen es sagt." Stattdessen dasselbe Raster wie auf der
            404, aber STILL, mit einer verdrehten Kachel in Vermillion: „Die 404
            sagt «eines ist nicht da», der Fehler sagt «eines ist
            schiefgegangen»."

        2 · KEIN VERSPRECHEN UEBER DIE EINGABEN. Hier stand nichts dergleichen,
            aber das Blatt nimmt seinen eigenen Vorschlag zurueck und die
            Begruendung gehoert hierher, damit sie niemand spaeter einbaut:
            „«Deine Eingaben sind nicht verloren» — das darf so nicht dastehen.
            Die Fehlerseite weiss nicht, was schiefging … Ein Trost, der einmal
            falsch ist, kostet mehr als er nuetzt — wer ihm glaubt und danach
            seine Arbeit sucht, glaubt der App beim naechsten Mal nichts mehr."
            Es gilt: „Die Anfrage kam nicht durch. Versuch es gleich noch
            einmal." Eine Feststellung ohne Zusage.

        3 · Seitentitel-Stufe (24/32 px) in zwei Gewichten, Text auf 40 ch, Inhalt mittig.
      */}
      <div className="m-auto flex w-full flex-col items-center gap-4 px-4 py-16 text-center">
        <FehlerRaster art="verdreht" />

        {/*
          🔴 `font-light` (300) — dieselbe Berichtigung wie in `not-found.tsx`,
          21.09.2026. P7 zeichnet die leichte Haelfte beider Fehlerseiten mit
          300; gebaut war 400. Festgehalten von check:masse (gelöscht mit T21), Probe 972.
        */}
        <h1 className="max-w-[40ch] text-xl leading-tight tracking-tight md:text-2xl">
          <span className="font-light">
            <Trans i18nKey="common:p7FehlerTeil1" />
          </span>{' '}
          <span className="font-bold">
            <Trans i18nKey="common:p7FehlerTeil2" />
          </span>
        </h1>

        <p className="text-muted-foreground max-w-[40ch] text-sm">
          <Trans i18nKey="common:p7FehlerText" />
        </p>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {/* Der Weg, der vorher fehlte — und bei einem Netzfehler der richtige. */}
          <Button variant="primary" size="lg" onClick={unstable_retry}>
            <Trans i18nKey="common:p7NochmalVersuchen" />
          </Button>

          {/*
            🔴 HARTE NAVIGATION, UND ZWAR ABSICHTLICH.

            Zwei Anlaeufe stehen hinter dieser einen Zeile.

            (1) Hier stand `reset(); router.push('/home'); router.refresh();`.
            Das `reset()` haengt die Grenze sofort wieder ein, sie wirft im
            selben Durchgang erneut, und der eben angestossene Wechsel wird mit
            verworfen. Gemessen am 03.09.2026 auf
            `/home/teams/user-settings/connections`: Klick, nichts passiert.

            (2) Danach stand hier `router.push('/home')` allein — und das ist an
            der WICHTIGSTEN Stelle ebenfalls tot. Next raeumt die Fehlergrenze
            AUSSCHLIESSLICH beim Pfadwechsel
            (`error-boundary.js:84`: `props.pathname !== state.previousPathname`).
            `/home` leitet auf den zuletzt benutzten Bereich weiter; tritt der
            Fehler also auf `/home/<bereich>` auf — der meistbesuchten Flaeche —
            endet der Weg auf demselben Pfad, auf dem er begann. Die Messung vom
            03.09. deckte genau diesen Fall NICHT ab: dort wechselte der Pfad.

            `window.location.assign` haengt an keiner dieser Bedingungen. Ein
            vollstaendiger Seitenaufbau ist auf einer Fehlerseite kein Nachteil,
            sondern das Gewuenschte: er raeumt zugleich jeden verdorbenen
            Client-Zustand, der zum Fehler gefuehrt haben kann.

            `/home` und nicht ein fester Bereich: die Strecke leitet auf den
            zuletzt benutzten Arbeitsbereich weiter (gemessen). Diese Seite
            kennt den Bereich nicht und darf ihn nicht raten.

            ⚠️ P7: „zweite Aktion nur Schrift" — `ghost` bleibt.
          */}
          <Button
            variant="ghost"
            onClick={() => window.location.assign('/home')}
          >
            <Trans i18nKey="common:p7ZurStartseite" />
          </Button>
        </div>

        {/*
          🔴 DIE KENNUNG IST GROEBER, ALS SIE AUSSIEHT — und P7 sagt, warum das
          die Aufschrift bestimmt:

            „der `digest` ist ein HASH DES FEHLERS, nicht die Kennung dieses
             Vorfalls. Zwei Nutzer mit demselben Programmfehler nennen dieselbe
             Kennung. Damit findet der Support die FEHLERART — aber nicht
             «dieser Kunde, gestern um 14:32»."

          Deshalb heisst sie „Kennung" und nicht „Ticket" oder „Vorfall-ID",
          und der Text daneben verspricht nichts ueber eine Antwort.
        */}
        {error.digest ? <FehlerKennung kennung={error.digest} /> : null}
      </div>
    </div>
  );
}

export default ErrorPage;
